import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiCloseLine, RiSearch2Fill } from "react-icons/ri";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { brandPalette } from "../../../constants/brandPalette";
import ReactSelect from "react-select";
import {
  getTopMissedQuestionsForAdminRequest,
  toggleMissedEnabledRequest,
  updateQuestionTagsRequest,
} from "../../../modules/admin-modules/questions-bank/Actions";
import { getAllTagsRequest } from "../../../modules/admin-modules/tags/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  muted: "rgba(30, 27, 23, 0.6)",
};

const QUESTION_IMAGE_BASE = "https://edutopiaonline.com/api";

const selectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

function calculateSimilarity(chapterName, tagName) {
  if (!chapterName || !tagName) return 0;

  const chapterLower = chapterName.toLowerCase();
  const tagLower = tagName.toLowerCase();

  if (chapterLower === tagLower) return 100;
  if (tagLower.includes(chapterLower)) return 80;
  if (chapterLower.includes(tagLower)) return 70;

  const chapterWords = chapterLower.split(/\s+/);
  const tagWords = tagLower.split(/\s+/);
  const commonWords = chapterWords.filter(
    (word) =>
      word.length > 2 &&
      tagWords.some((tagWord) => tagWord.includes(word) || word.includes(tagWord))
  );

  if (commonWords.length > 0) {
    return 50 + commonWords.length * 10;
  }

  let maxMatch = 0;
  for (const word of chapterWords) {
    if (word.length > 2 && tagLower.includes(word)) {
      maxMatch = Math.max(maxMatch, 40);
    }
  }

  return maxMatch;
}

function isMissedEnabled(value) {
  return value === true || value === 1 || value === "1";
}

function readMissedEnabled(item, question) {
  if (item && Object.prototype.hasOwnProperty.call(item, "missed_enabled")) {
    return item.missed_enabled;
  }
  if (question && Object.prototype.hasOwnProperty.call(question, "missed_enabled")) {
    return question.missed_enabled;
  }
  return false;
}

const TagMissedQuestions = () => {
  const dispatch = useDispatch();

  const topMissedQuestions = useSelector((state) => state.questionsBank.topMissedQuestions);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  const tags = useSelector((state) => state.adminTags.tags);
  const tagsLoading = useSelector((state) => state.adminTags.isLoading);
  const updateLoading = useSelector((state) => state.questionsBank.updateTagsLoading);
  const toggleMissedEnabledId = useSelector(
    (state) => state.questionsBank.toggleMissedEnabledId
  );

  const [questionTags, setQuestionTags] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTags, setFilterTags] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    dispatch(getTopMissedQuestionsForAdminRequest());
    dispatch(getAllTagsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!topMissedQuestions?.length) return;

    setQuestionTags((prevTags) => {
      const newTags = { ...prevTags };
      topMissedQuestions.forEach((item) => {
        const question = item.question || item;
        if (!question.id) return;

        if (!newTags[question.id]) {
          newTags[question.id] = question.tags || [];
          return;
        }

        const serverTags = question.tags || [];
        const currentTagIds = (newTags[question.id] || []).map((t) => t.id).sort();
        const serverTagIds = serverTags.map((t) => t.id).sort();
        if (JSON.stringify(currentTagIds) === JSON.stringify(serverTagIds)) {
          newTags[question.id] = serverTags;
        }
      });
      return newTags;
    });
  }, [topMissedQuestions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterTags, itemsPerPage]);

  const handleAddTag = (questionId, tagId) => {
    const currentTags = questionTags[questionId] || [];
    if (currentTags.find((tag) => tag.id === tagId)) return;
    const tagToAdd = tags.find((tag) => tag.id === tagId);
    if (!tagToAdd) return;
    setQuestionTags({
      ...questionTags,
      [questionId]: [...currentTags, tagToAdd],
    });
  };

  const handleRemoveTag = (questionId, tagId) => {
    const currentTags = questionTags[questionId] || [];
    setQuestionTags({
      ...questionTags,
      [questionId]: currentTags.filter((tag) => tag.id !== tagId),
    });
  };

  const handleUpdateTags = (questionId) => {
    const tagsToUpdate = questionTags[questionId] || [];
    const tagIds = tagsToUpdate.map((tag) => tag.id);
    dispatch(updateQuestionTagsRequest(questionId, tagIds));
  };

  const handleToggleMissedEnabled = (questionId) => {
    dispatch(toggleMissedEnabledRequest(questionId));
  };

  const tagsOptions = useMemo(
    () =>
      tags?.map((tag) => ({
        value: tag.id,
        label: tag.name,
      })) ?? [],
    [tags]
  );

  const filteredQuestions = useMemo(() => {
    const list = Array.isArray(topMissedQuestions) ? topMissedQuestions : [];
    const q = searchTerm.trim().toLowerCase();
    const selectedTagIds = filterTags.map((t) => t.value);

    return list.filter((item) => {
      const question = item.question || item;
      if (q && !String(question.question || "").toLowerCase().includes(q)) {
        return false;
      }
      if (selectedTagIds.length > 0) {
        const activeTags = questionTags[question.id] || question.tags || [];
        const hasSelectedTag = activeTags.some((tag) =>
          selectedTagIds.some((id) => String(id) === String(tag.id))
        );
        if (!hasSelectedTag) return false;
      }
      return true;
    });
  }, [topMissedQuestions, searchTerm, filterTags, questionTags]);

  const totalPages = Math.max(1, Math.ceil(filteredQuestions.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQuestions = filteredQuestions.slice(startIndex, endIndex);

  const renderQuestionCard = (item, index) => {
    const question = item.question || item;
    const wrongCount = item.wrong_count || 0;
    const questionId = question.id;
    const currentTags = questionTags[questionId] || [];
    const chapter = item.chapter || question.chapter;
    const questionNumber = startIndex + index + 1;
    const missedEnabled = isMissedEnabled(readMissedEnabled(item, question));
    const isTogglingMissed = String(toggleMissedEnabledId) === String(questionId);

    const availableTags =
      tags
        ?.filter((tag) => !currentTags.find((t) => t.id === tag.id))
        .map((tag) => ({
          ...tag,
          similarity: chapter ? calculateSimilarity(chapter.name, tag.name) : 0,
        }))
        .sort((a, b) => b.similarity - a.similarity) || [];

    return (
      <Box key={questionId || index} {...cardSx} p={{ base: 4, md: 6 }}>
        <Flex justify="space-between" align="start" mb={4} gap={3} flexWrap="wrap">
          <HStack spacing={2} flexWrap="wrap">
            <Badge
              bg="rgba(91, 13, 22, 0.1)"
              color={C.primary}
              px={3}
              py={1}
              borderRadius="full"
              fontWeight="800"
              fontSize="xs"
            >
              #{questionNumber}
            </Badge>
            {chapter ? (
              <Badge
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                border="1px solid"
                borderColor="var(--border-subtle)"
                px={3}
                py={1}
                borderRadius="full"
                fontWeight="700"
                fontSize="xs"
              >
                {chapter.name}
              </Badge>
            ) : null}
            {wrongCount > 0 ? (
              <Badge
                bg="rgba(155, 44, 44, 0.12)"
                color="#9B2C2C"
                px={3}
                py={1}
                borderRadius="full"
                fontWeight="800"
                fontSize="xs"
              >
                {wrongCount} students missed
              </Badge>
            ) : null}
            <Badge
              bg={missedEnabled ? "rgba(0, 170, 141, 0.12)" : "var(--surface-muted)"}
              color={missedEnabled ? "#00AA8D" : "var(--text-muted)"}
              border="1px solid"
              borderColor={missedEnabled ? "rgba(0, 170, 141, 0.35)" : "var(--border-subtle)"}
              px={3}
              py={1}
              borderRadius="full"
              fontWeight="800"
              fontSize="xs"
            >
              {missedEnabled ? "Missed enabled" : "Missed disabled"}
            </Badge>
          </HStack>
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            fontWeight="800"
            px={4}
            borderColor={missedEnabled ? "#9B2C2C" : C.primary}
            color={missedEnabled ? "#9B2C2C" : C.primary}
            isLoading={isTogglingMissed}
            loadingText="Updating…"
            onClick={() => handleToggleMissedEnabled(questionId)}
            _hover={{
              bg: missedEnabled ? "rgba(155, 44, 44, 0.08)" : "rgba(91, 13, 22, 0.08)",
            }}
          >
            {missedEnabled ? "Disable missed" : "Enable missed"}
          </Button>
        </Flex>

        {question.question ? (
          <Box
            mb={4}
            fontSize="md"
            fontWeight="600"
            color="var(--text-primary)"
            sx={{
              "& p, & span, & div, & li": { color: "inherit" },
            }}
            dangerouslySetInnerHTML={{ __html: question.question }}
          />
        ) : null}

        {question.image ? (
          <Image
            src={`${QUESTION_IMAGE_BASE}/${question.image}`}
            alt="Question"
            maxW="100%"
            maxH="240px"
            objectFit="contain"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="var(--border-subtle)"
            mb={4}
          />
        ) : null}

        <VStack align="stretch" spacing={4} mt={2}>
          <Box>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
              Active tags
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {currentTags.length > 0 ? (
                currentTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    bg={`${C.primary}14`}
                    color={C.primary}
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="700"
                    display="inline-flex"
                    alignItems="center"
                    gap={1}
                  >
                    {tag.name}
                    <IconButton
                      icon={<RiCloseLine size={14} />}
                      size="xs"
                      variant="ghost"
                      minW="20px"
                      h="20px"
                      aria-label="Remove tag"
                      color={C.primary}
                      onClick={() => handleRemoveTag(questionId, tag.id)}
                      _hover={{ bg: C.primary, color: "white" }}
                    />
                  </Badge>
                ))
              ) : (
                <Text fontSize="sm" color="var(--text-muted)" fontWeight="600">
                  No tags assigned
                </Text>
              )}
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
              Add tag
            </Text>
            <ReactSelect
              options={tagsOptions}
              onChange={(selected) => {
                if (selected) handleAddTag(questionId, selected.value);
              }}
              styles={selectStyles}
              placeholder="Select a tag…"
              isClearable
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              value={null}
            />
          </Box>

          {availableTags.length > 0 ? (
            <Box>
              <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                Suggested tags
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                {availableTags.slice(0, 5).map((tag) => (
                  <Badge
                    key={tag.id}
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="700"
                    cursor="pointer"
                    bg="var(--surface-muted)"
                    color="var(--text-primary)"
                    border="1px solid"
                    borderColor="var(--border-subtle)"
                    _hover={{ borderColor: C.primary, color: C.primary }}
                    onClick={() => handleAddTag(questionId, tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </HStack>
            </Box>
          ) : null}

          <Flex justify="flex-end" mt={2}>
            <Button
              bg={C.primary}
              color={brandPalette.cream}
              fontWeight="800"
              borderRadius="full"
              px={6}
              onClick={() => handleUpdateTags(questionId)}
              isLoading={updateLoading}
              loadingText="Updating…"
              _hover={{ bg: C.secondary }}
            >
              Update tags
            </Button>
          </Flex>
        </VStack>
      </Box>
    );
  };

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
        dir="ltr"
      >
        <Flex
          w="100%"
          mb={8}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text
              fontSize={{ base: "28px", md: "40px" }}
              fontWeight="800"
              color={C.primary}
              fontFamily="'Amiri', 'Tajawal', sans-serif"
              lineHeight="1.1"
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              Tag Missed Questions
            </Text>
            <Text mt={2} fontSize="sm" fontWeight="600" color="var(--text-muted)" maxW="2xl">
              Review the most missed questions and assign tags so students can practice weaker areas.
            </Text>
            <Text mt={2} fontSize="sm" fontWeight="700" color={C.secondary}>
              Showing {filteredQuestions.length} question{filteredQuestions.length === 1 ? "" : "s"}
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex mb={6} gap={4} flexWrap="wrap" align="flex-end">
          <Box flex="1" minW={{ base: "100%", md: "280px" }} maxW={{ md: "420px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
              Search
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="full">
                <RiSearch2Fill color="rgba(91, 13, 22, 0.5)" fontSize="18px" />
              </InputLeftElement>
              <Input
                placeholder="Search questions…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="full"
                border="1px solid"
                borderColor="var(--border-subtle)"
                bg="var(--surface-raised)"
                color="var(--text-primary)"
                h="44px"
                pl={10}
                _placeholder={{ color: "var(--text-muted)" }}
                _focus={{
                  borderColor: C.primary,
                  boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.2)",
                }}
              />
            </InputGroup>
          </Box>

          <Box flex="1" minW={{ base: "100%", md: "280px" }} maxW={{ md: "420px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
              Filter by tags
            </Text>
            <ReactSelect
              isMulti
              options={tagsOptions}
              value={filterTags}
              onChange={(selected) => setFilterTags(selected || [])}
              styles={selectStyles}
              placeholder="All tags…"
              isClearable
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </Box>
        </Flex>

        {isLoading || tagsLoading ? (
          <Box {...cardSx} p={10} textAlign="center">
            <Spinner size="lg" color={C.primary} mb={3} />
            <Text fontSize="md" fontWeight="700" color="var(--text-primary)">
              Loading missed questions…
            </Text>
          </Box>
        ) : filteredQuestions.length > 0 ? (
          <VStack align="stretch" spacing={5}>
            {paginatedQuestions.map((item, index) => renderQuestionCard(item, index))}

            {filteredQuestions.length > 5 ? (
              <Box {...cardSx} p={{ base: 4, md: 5 }}>
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="var(--text-muted)" fontWeight="600">
                      Items per page
                    </Text>
                    <Select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      size="sm"
                      w="88px"
                      borderRadius="lg"
                      borderColor="var(--border-subtle)"
                      bg="var(--surface-muted)"
                      color="var(--text-primary)"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Select>
                  </HStack>

                  <Text fontSize="sm" color="var(--text-muted)" fontWeight="600">
                    {startIndex + 1}–{Math.min(endIndex, filteredQuestions.length)} of{" "}
                    {filteredQuestions.length}
                  </Text>

                  <HStack spacing={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      leftIcon={<MdChevronLeft size={18} />}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      isDisabled={currentPage === 1}
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      color="var(--text-primary)"
                      borderRadius="full"
                      fontWeight="700"
                    >
                      Previous
                    </Button>

                    <HStack spacing={1}>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          const isActive = currentPage === pageNum;
                          return (
                            <Button
                              key={pageNum}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              minW="40px"
                              borderRadius="full"
                              fontWeight="800"
                              bg={isActive ? C.primary : "transparent"}
                              color={isActive ? brandPalette.cream : "var(--text-primary)"}
                              border="1px solid"
                              borderColor={isActive ? C.primary : "var(--border-subtle)"}
                              _hover={{
                                bg: isActive ? C.secondary : "var(--surface-muted)",
                              }}
                            >
                              {pageNum}
                            </Button>
                          );
                        }
                        if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return (
                            <Text key={pageNum} px={1} color="var(--text-muted)">
                              …
                            </Text>
                          );
                        }
                        return null;
                      })}
                    </HStack>

                    <Button
                      size="sm"
                      rightIcon={<MdChevronRight size={18} />}
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      isDisabled={currentPage === totalPages}
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      color="var(--text-primary)"
                      borderRadius="full"
                      fontWeight="700"
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </Box>
            ) : null}
          </VStack>
        ) : (
          <Box {...cardSx} p={10} textAlign="center">
            <Text fontSize="md" fontWeight="700" color="var(--text-muted)">
              {searchTerm.trim() || filterTags.length > 0
                ? "No missed questions match your filters."
                : "No missed questions found."}
            </Text>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default TagMissedQuestions;
